import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageSquare } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import VerifiedBadge from "@/components/VerifiedBadge";

function initials(name = "?") { return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(); }
function timeAgo(iso) { try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; } }

export default function Chat() {
  const { user } = useAuth();
  const { convId } = useParams();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const pollRef = useRef(null);
  const bottomRef = useRef(null);

  const loadConvs = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConvs(data);
      if (convId) {
        const found = data.find((c) => c.id === convId);
        if (found) setActive(found);
      } else if (data.length > 0 && !active) {
        setActive(data[0]);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadMessages = async (id) => {
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setMessages(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch {}
  };

  useEffect(() => { loadConvs(); /* eslint-disable-next-line */ }, [convId]);

  useEffect(() => {
    if (!active) return;
    loadMessages(active.id);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadMessages(active.id), 5000);
    return () => clearInterval(pollRef.current);
  }, [active?.id]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${active.id}/messages`, { content: text });
      setMessages((m) => [...m, data]);
      setText("");
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      loadConvs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div>
        <div className="label-eyebrow">Chat</div>
        <h1 className="font-serif-display text-3xl text-[color:var(--ayur-emerald)]">Private messages</h1>
      </div>

      <div className="mt-6 paper-card overflow-hidden grid md:grid-cols-[280px_1fr] h-[70vh]">
        <div className="border-r border-[color:var(--ayur-border)] overflow-y-auto" data-testid="conversations-list">
          {convs.length === 0 && (
            <div className="p-6 text-center text-sm text-[color:var(--ayur-mute)]">
              No conversations yet.
              <div className="mt-3">
                <Link to="/app/discover" className="text-[color:var(--ayur-terracotta)] link-underline">Find a scholar to message →</Link>
              </div>
            </div>
          )}
          {convs.map((c) => (
            <button
              key={c.id}
              onClick={() => { setActive(c); navigate(`/app/chat/${c.id}`); }}
              data-testid="conversation-item"
              className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-[color:var(--ayur-border)] hover:bg-[color:var(--ayur-cream-2)] transition ${
                active?.id === c.id ? "bg-[color:var(--ayur-cream-2)]" : ""
              }`}
            >
              <Avatar className="w-10 h-10">
                <AvatarImage src={resolveImageUrl(c.peer?.avatar_url)} />
                <AvatarFallback className="bg-[color:var(--ayur-badge)] text-[color:var(--ayur-emerald)] text-xs">
                  {initials(c.peer?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate inline-flex items-center gap-1">
                  {c.peer?.full_name}
                  {c.peer?.verified && <VerifiedBadge size={12} />}
                </div>
                <div className="text-xs text-[color:var(--ayur-mute)] truncate">
                  {c.last_message ? c.last_message.content : "Say hello…"}
                </div>
              </div>
              {c.unread_count > 0 && (
                <span className="min-w-[20px] h-5 px-1 rounded-full bg-[color:var(--ayur-terracotta)] text-white text-[10px] font-bold flex items-center justify-center">
                  {c.unread_count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-col">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-[color:var(--ayur-mute)]">
              <MessageSquare className="w-12 h-12 mb-3" />
              <div className="font-serif-display text-xl">Pick a conversation</div>
              <div className="text-sm mt-1">or start one from Discover / a scholar profile.</div>
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-[color:var(--ayur-border)] flex items-center gap-3">
                <Avatar className="w-9 h-9">
                  <AvatarImage src={resolveImageUrl(active.peer?.avatar_url)} />
                  <AvatarFallback className="bg-[color:var(--ayur-badge)] text-[color:var(--ayur-emerald)] text-xs">
                    {initials(active.peer?.full_name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-semibold inline-flex items-center gap-1">
                    <Link to={`/app/scholars/${active.peer?.id}`} className="link-underline">{active.peer?.full_name}</Link>
                    {active.peer?.verified && <VerifiedBadge size={14} />}
                  </div>
                  <div className="text-xs text-[color:var(--ayur-mute)]">{active.peer?.degree}{active.peer?.specialty ? ` · ${active.peer.specialty}` : ""}</div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="messages-list">
                {messages.length === 0 && (
                  <div className="text-center text-sm text-[color:var(--ayur-mute)] mt-8">No messages yet. Send the first one.</div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        mine
                          ? "bg-[color:var(--ayur-emerald)] text-white rounded-br-sm"
                          : "bg-[color:var(--ayur-cream-2)] text-[color:var(--ayur-ink)] rounded-bl-sm"
                      }`}>
                        <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[color:var(--ayur-mute)]"}`}>{timeAgo(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={send} className="p-4 border-t border-[color:var(--ayur-border)] flex gap-2">
                <Input
                  value={text} onChange={(e) => setText(e.target.value)}
                  placeholder="Write a message…" className="bg-white"
                  data-testid="message-input"
                />
                <Button type="submit" disabled={sending}
                  data-testid="message-send-button"
                  className="bg-[color:var(--ayur-emerald)] hover:bg-[color:var(--ayur-emerald-2)] text-white">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
